
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  error: string | null;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
  error: null,
  refreshSession: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const signOut = useCallback(async () => {
    try {
      console.log("[AuthProvider] Signing out user");
      setLoading(true);
      
      const { error: signOutError } = await supabase.auth.signOut();
      
      if (signOutError) {
        console.error("[AuthProvider] Sign out error:", signOutError);
        throw signOutError;
      }
      
      setSession(null);
      setUser(null);
      setError(null);
      toast.success("Signed out successfully");
    } catch (err: any) {
      console.error("[AuthProvider] Sign out error:", err);
      setError(err.message || "Failed to sign out");
      toast.error(err.message || "Failed to sign out");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      console.log("[AuthProvider] Manually refreshing session");
      setLoading(true);
      
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.error("[AuthProvider] Session refresh error:", refreshError);
        throw refreshError;
      }
      
      console.log("[AuthProvider] Session refreshed successfully:", !!data.session);
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setError(null);
    } catch (err: any) {
      console.error("[AuthProvider] Session refresh error:", err);
      setError(err.message || "Failed to refresh session");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize auth and set up listeners
  useEffect(() => {
    console.log("[AuthProvider] Initializing auth system");
    let mounted = true;
    
    // First, check for existing session
    const checkSession = async () => {
      try {
        console.log("[AuthProvider] Checking for existing session");
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("[AuthProvider] Get session error:", error);
          if (mounted) setError(error.message);
          return;
        }
        
        if (mounted) {
          const hasSession = !!data.session;
          console.log("[AuthProvider] Initial session:", hasSession ? "Found" : "None");
          setSession(data.session);
          setUser(data.session?.user ?? null);
        }
      } catch (err: any) {
        console.error("[AuthProvider] Session check error:", err);
        if (mounted) setError(err.message || "Failed to check session");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      console.log("[AuthProvider] Auth state changed:", event, newSession?.user?.id || "no user");
      
      if (!mounted) return;
      
      if (event === 'SIGNED_IN') {
        console.log("[AuthProvider] User signed in:", newSession?.user?.id);
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setError(null);
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        console.log("[AuthProvider] User signed out");
        setSession(null);
        setUser(null);
        setLoading(false);
      } else if (event === 'TOKEN_REFRESHED') {
        console.log("[AuthProvider] Token refreshed");
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
      } else if (event === 'USER_UPDATED') {
        console.log("[AuthProvider] User updated");
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
      }
    });
    
    // Check session immediately
    checkSession();
    
    // Cleanup function
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);
  
  // Debug logging
  useEffect(() => {
    console.log("[AuthProvider] Auth state updated:", { 
      authenticated: !!user, 
      userId: user?.id || "none",
      sessionExpires: session?.expires_at 
        ? new Date(session.expires_at * 1000).toLocaleString() 
        : 'No session'
    });
  }, [user, session]);

  const value = {
    session,
    user,
    loading,
    signOut,
    error,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
